// src/components/OfficeForm.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error403Page from "./Error_backend";
import api from "../api.js";
import "../style/css/AppointmentForm.css";

interface OfficeFormData {
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  country: string;
  email: string;
  phone: string;
  website: string;
}

const OfficeForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<OfficeFormData>({
    name: "",
    description: "",
    address: "",
    city: "",
    state: "",
    country: "México",
    email: "",
    phone: "",
    website: "",
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // API base URL - using /services endpoint as specified
  const API_BASE = "/offices";

  // Validate form data
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!formData.name.trim()) {
      errors.name = "El nombre del consultorio es requerido";
    }

    if (!formData.address.trim()) {
      errors.address = "La dirección es requerida";
    }

    if (!formData.city.trim()) {
      errors.city = "La ciudad es requerida";
    }

    if (!formData.state.trim()) {
      errors.state = "El estado es requerido";
    }

    if (!formData.country) {
      errors.country = "El país es requerido";
    }

    // Email validation (optional but must be valid if provided)
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      errors.email = "El formato del email no es válido";
    }

    // Website validation (optional but must be valid if provided)
    if (
      formData.website.trim() &&
      !/^https?:\/\/.+\..+/.test(formData.website)
    ) {
      errors.website =
        "El formato del sitio web no es válido (debe empezar con http:// o https://)";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Fetch office data if in edit mode
  useEffect(() => {
    const fetchOffice = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`${API_BASE}/${id}/`);
        if (!response) {
          throw new Error("Failed to fetch office");
        }

        const officeData = await response.data;

        // Format the data for the form
        setFormData({
          name: officeData.name || "",
          description: officeData.description || "",
          address: officeData.address || "",
          city: officeData.city || "",
          state: officeData.state || "",
          country: officeData.country || "México",
          email: officeData.email || "",
          phone: officeData.phone || "",
          website: officeData.website || "",
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchOffice();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setError("Por favor, corrija los errores en el formulario");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let response;
      if (isEditMode) {
        response = await api.put(`${API_BASE}/${id}/`, formData);
      } else {
        response = await api.post(`${API_BASE}/`, formData);
      }

      if (!response) {
        throw new Error(`Failed to ${isEditMode ? "update" : "create"} office`);
      }

      navigate("/offices"); // Adjust to your offices list route
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!isEditMode || !id) {
      return;
    }

    if (
      !window.confirm("¿Estás seguro de que quieres eliminar este consultorio?")
    ) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const response = await api.delete(`${API_BASE}/${id}/`);

      if (!response) {
        throw new Error("Failed to delete office");
      }

      navigate("/offices");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const handleBackClick = () => {
    navigate("/offices"); // Adjust to your offices list route
  };

  // Show loading state
  if (loading) {
    return (
      <div className="form-entire-container-form-appointment">
        <div className="form-container-form-appointment">
          <div className="loading-message-form-appointment">
            <i className="fas fa-spinner fa-spin"></i> Cargando consultorio...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && isEditMode) {
    return (
      <Error403Page
        code={500}
        title="Recurso no disponible."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  const titleText = isEditMode
    ? "Editar Consultorio"
    : "Crear Nuevo Consultorio";

  return (
    <div className="form-entire-container-form-appointment">
      <div className="form-container-form-appointment">
        <div className="header-actions-form-appointment">
          <button
            className="back-link-form-appointment"
            onClick={handleBackClick}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <i className="fas fa-arrow-left"></i> Volver a Consultorios
          </button>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>
            <i
              className="fas fa-building"
              style={{ color: "var(--color-accent)" }}
            ></i>{" "}
            {titleText}
          </h2>
          <span></span>
        </div>

        <div className="form-card-form-appointment">
          <h3 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
            {isEditMode ? `Editando: ${formData.name}` : "Nuevo consultorio"}
          </h3>

          {error && (
            <div className="message-box-form-appointment message-error-form-appointment">
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-group-form-appointment">
              <label htmlFor="name">Nombre del Consultorio *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={saving || deleting}
                placeholder="Ej: Consultorio Central, Sucursal Norte..."
                className={formErrors.name ? "input-error" : ""}
              />
              {formErrors.name && (
                <span className="field-error">{formErrors.name}</span>
              )}
            </div>

            <div className="form-group-form-appointment">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                placeholder="Ej: Consultorio principal ubicado en el centro de la ciudad con todas las especialidades..."
                value={formData.description}
                onChange={handleChange}
                disabled={saving || deleting}
              ></textarea>
            </div>

            {/* Address Information */}
            <div className="form-group-form-appointment">
              <label htmlFor="address">Dirección *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                disabled={saving || deleting}
                placeholder="Ej: Av. Principal #123, Col. Centro"
                className={formErrors.address ? "input-error" : ""}
              />
              {formErrors.address && (
                <span className="field-error">{formErrors.address}</span>
              )}
            </div>

            <div className="grid-half-form-appointment">
              <div className="form-group-form-appointment">
                <label htmlFor="city">Ciudad *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting}
                  placeholder="Ej: Ciudad de México"
                  className={formErrors.city ? "input-error" : ""}
                />
                {formErrors.city && (
                  <span className="field-error">{formErrors.city}</span>
                )}
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="state">Estado *</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  disabled={saving || deleting}
                  placeholder="Ej: CDMX"
                  className={formErrors.state ? "input-error" : ""}
                />
                {formErrors.state && (
                  <span className="field-error">{formErrors.state}</span>
                )}
              </div>
            </div>

            <div className="form-group-form-appointment">
              <label htmlFor="country">País *</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                disabled={saving || deleting}
                className={formErrors.country ? "input-error" : ""}
              >
                <option value="México">México</option>
              </select>
              {formErrors.country && (
                <span className="field-error">{formErrors.country}</span>
              )}
            </div>

            {/* Contact Information */}
            <div className="grid-half-form-appointment">
              <div className="form-group-form-appointment">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={saving || deleting}
                  placeholder="Ej: contacto@consultorio.com"
                  className={formErrors.email ? "input-error" : ""}
                />
                {formErrors.email && (
                  <span className="field-error">{formErrors.email}</span>
                )}
              </div>

              <div className="form-group-form-appointment">
                <label htmlFor="phone">Teléfono</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={saving || deleting}
                  placeholder="Ej: +52 55 1234 5678"
                />
              </div>
            </div>

            <div className="form-group-form-appointment">
              <label htmlFor="website">Sitio Web</label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                disabled={saving || deleting}
                placeholder="Ej: https://www.consultorio.com"
                className={formErrors.website ? "input-error" : ""}
              />
              {formErrors.website && (
                <span className="field-error">{formErrors.website}</span>
              )}
            </div>

            <hr
              style={{
                borderColor: "var(--neutral-200)",
                margin: "var(--space-md) 0",
              }}
            />

            <div className="action-footer-form-appointment">
              {isEditMode && (
                <button
                  type="button"
                  className="btn--delete-form-appointment"
                  onClick={handleDeleteClick}
                  disabled={saving || deleting}
                >
                  <i className="fas fa-trash-alt"></i>
                  {deleting ? "Eliminando..." : "Eliminar Consultorio"}
                </button>
              )}
              <button
                type="submit"
                className={`btn btn--primary btn--large btn--save-form-appointment ${
                  !isEditMode ? "btn--full-width-in-create" : ""
                }`}
                disabled={saving || deleting}
              >
                <i className="fas fa-save"></i>{" "}
                {saving
                  ? isEditMode
                    ? "Guardando..."
                    : "Creando..."
                  : isEditMode
                    ? "Guardar Cambios"
                    : "Crear Consultorio"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OfficeForm;
